import type { EssayKind } from "./program-types";

export const writingGuide: Record<EssayKind, { title: string; purpose: string; target: string; paragraphs: string[]; prompts: string[]; avoid: string; template: string }> = {
  sop: {
    title: "SOP · 学术目的陈述", target: "母稿约900–1000英文词；提交时按各校字数或页数调整",
    purpose: "让评审看清你如何思考科学问题、过去的研究怎样准备了你，以及为什么需要这个项目的博士训练。主线：临床问题 → 疾病机制 → 多组学发现与实验验证；AI体现为研究工具。",
    paragraphs: [
      "第1段｜研究动机（约80–100词）：用一个真实的研究疑问引出对疾病机制的兴趣。可从糖尿病与乳腺癌之间的生物学联系切入；别把未做过的临床观察写成亲历。",
      "第2段｜肿瘤研究基础（约180–220词）：讲孙立栋老师课题组中的一项具体工作。写问题、你的任务、分析/实验选择、发现与解释。区分论文共同作者贡献和自己提出的项目。",
      "第3段｜主要项目与独立判断（约280–330词）：用痛风多组学项目证明主动性。为什么提出问题？为何选择这些数据？怎样排查队列差异、混杂或不支持假设的证据？写一个真正发生的调整。说明结果支持什么、尚不能证明什么。申请肿瘤方向也可以用其他疾病项目展示科研能力。",
      "第4段｜技术融合（约80–100词）：挑自动染色图像分析或MedReader之一，写需求、开发、验证及局限。只有测过才写效率提升数字。AI协助编程与研究者的科学判断分别说明。",
      "第5段｜未来研究问题（约100–120词）：可围绕肿瘤细胞状态、微环境与治疗抵抗提出问题；说明需要多组学与功能扰动的原因。保留轮转探索空间。",
      "第6段｜项目匹配与目标（约100–130词）：连接项目训练、平台与未来问题。导师人数依学校要求：Stanford另有8–12位排序字段，Harvard BBS不要求预先锁定导师。收尾说明希望成长为什么样的研究者。",
    ],
    prompts: ["我最想解释的一个疾病现象是什么？", "哪一次判断体现了我的贡献，而不是软件的默认输出？", "哪个结果改变了我原先的解释？", "为什么这个项目的训练能补上我当前的能力缺口？"],
    avoid: "避免把CV改成长段落；避免IF清单、宏大技术史、未经完成的因果验证。只有真实需要说明且系统允许时才交代成绩背景。",
    template: "【研究动机】\n我在[真实项目/训练]中注意到[具体问题]。这一问题让我希望理解[疾病生物学机制]。\n\n【肿瘤科研基础】\n在孙立栋老师指导下，我参与[准确项目]。课题试图回答[问题]，我具体负责[贡献]。采用[方法]是因为[理由]；结果显示[已确认发现]，其局限是[边界]。\n\n【主要项目：独立判断】\n在痛风微针前期调研中，我提出[问题]。我整合[数据]，并通过[真实质控/验证]检查[偏差]。当[实际困难]出现时，我决定[调整]，依据是[证据]。最终支持[发现]，但尚不能推出[因果/机制结论]。\n\n【工具与研究实践】\n为解决[具体需求]，我开发[工具]，通过[真实验证方式]检查结果。AI帮助[具体环节]，而我负责[科学判断和核验]。\n\n【未来研究与项目匹配】\n博士阶段，我希望研究[肿瘤问题]，结合[方法与实验]。贵项目的[训练/平台]能帮助我[能力目标]；[导师/研究群体]与这一问题相关。我的长期目标是[真实职业目标]。"
  },
  research: {
    title: "Research Statement · 科研陈述", target: "母稿约900–1000英文词；根据项目题目取舍",
    purpose: "展开最能代表你的研究经历，证明你理解研究逻辑、个人贡献与证据边界。通常主要描述既往研究，是否写未来计划由题目决定。",
    paragraphs: [
      "背景与问题（约100词）：领域中存在什么尚未解释的现象，为什么重要，问题如何从前期工作中产生。",
      "研究设计（约150词）：主要假设、对照/比较对象、数据来源与方法选择理由。不要把所有技术按顺序堆上去。",
      "个人贡献与关键判断（约250词）：明确导师、团队、AI和你各自负责什么；用一次流程修正或反例检验说明独立性。",
      "结果与证据（约250词）：挑2–3个核心发现。写出实际方向、验证方式与可重复性；没有具体数据时先留待填标记。",
      "局限与下一步（约150词）：观察性关联、细胞来源定位与因果机制分开；指出需要什么实验或独立队列来检验。",
      "成长与补充经历（约100词）：说明研究改变了你的思考方式，可简述肿瘤实验基础如何补充计算研究。",
    ],
    prompts: ["研究的最小可检验问题是什么？", "我控制或检查了哪些混杂与批次效应？", "最重要的发现具体是什么，证据来自哪里？", "下一项实验的对照、读出和可能解释是什么？"],
    avoid: "不要把单细胞细胞通讯推断写成实验证明，不把不同队列的横断面对比写成已观察到的个体进展。不要把拟开展工作写成已完成。",
    template: "【研究问题与背景】\n项目名称：[ ]\n核心问题：[ ]\n问题来源和重要性：[ ]\n\n【研究设计】\n假设/比较对象：[ ]\n数据、实验和方法及理由：[ ]\n\n【我的贡献】\n我独立完成：[ ]\n导师/合作者指导或完成：[ ]\nAI协助及我的验证：[ ]\n\n【关键判断】\n困难或反例：[ ]\n我的调整与理由：[ ]\n\n【结果】\n发现一与证据：[ ]\n发现二与证据：[ ]\n验证方式与复现材料：[ ]\n\n【局限与下一步】\n当前不能回答：[ ]\n可检验的下一步及对照：[ ]\n这段经历如何改变我的研究思路：[ ]"
  },
  personal: {
    title: "Personal Statement · 个人经历陈述", target: "母稿约400–500英文词；具体名称不等于固定内容，优先读题",
    purpose: "用真实经历呈现好奇心、持续学习、协作和贡献他人的方式。若学校用Personal Statement同时询问科研与项目匹配，应按实际题目写。",
    paragraphs: [
      "起点（约80词）：从孙军老师的文献汇报课讲起，描述你如何从阅读结果转向追问证据与研究设计。",
      "持续行动（约130词）：持续阅读、在兴知集分享、受邀制作示范汇报；挑一个具体帮助同学理解论文的例子。",
      "跨领域学习（约130词）：从遇到文献/实验流程问题，到自学开发并制作MedReader或LabGuide。展示学习过程与反馈，不只报竞赛奖项。",
      "合作与贡献（约100词）：带队如何协调任务、吸收不同意见、共享工具；写未来愿意开展的journal club、可复现分析分享或同伴支持。",
      "研究动机（约60词）：这些经历如何让你选择博士训练、希望给学术共同体带来什么。",
    ],
    prompts: ["哪一个具体情境使我改变了阅读或研究方式？", "我如何根据同学或用户反馈修改工具？", "我为团队承担了什么具体责任？"],
    avoid: "不虚构困难或身份经历；不复制SOP的大段研究方法；不把孙军老师写成带你做湿实验的导师。",
    template: "【起点】\n在孙军老师的文献汇报课上，[真实经历]使我开始[改变]。\n\n【持续学习与分享】\n此后我通过[具体行动]持续阅读和分享论文。一次[真实分享/指导]让我意识到[认识]。\n\n【跨领域行动】\n面对[实际问题]，我学习[技能]并开发[工具]。我收到[真实反馈]后，调整了[内容]。\n\n【合作与未来贡献】\n在[团队经历]中，我承担[责任]，学会[认识]。进入研究生阶段，我希望通过[具体活动]参与学术共同体。"
  },
  community: {
    title: "Community Statement · 社区贡献", target: "母稿150–200英文词",
    purpose: "回答你的经历和视角将如何影响项目共同体，以及这个共同体如何拓展你。双向写，行动写具体。",
    paragraphs: ["约60词：举一个真实帮助同伴的例子，如文献汇报指导、开源图像分析流程或共享实验经验。", "约60词：说明这使你形成了怎样的合作习惯，例如把不同专业的语言讲明白、让工具可追溯、认真接收使用反馈。", "约60词：结合目标学校真实存在的活动或平台，说明计划如何参与及想向他人学习什么；未核实活动名称先留空。"],
    prompts: ["我实际帮助过谁、做了什么？", "我进入项目后能参与哪项真实活动？", "我希望从不同背景的同伴身上学到什么？"],
    avoid: "避免泛泛写开放包容；不要虚构社团名称或承诺未经确认的活动。",
    template: "我曾通过[文献分享/工具共享/带队中的具体行动]帮助[对象]解决[问题]。这段经历让我学会[合作方式]。\n\n在[项目]中，我希望参与[已核实活动]，贡献[具体方式]；同时向[不同背景的同伴]学习[内容]，拓展我对[研究/合作]的理解。"
  },
  short: {
    title: "Short Answer · 科学问题与补充短答", target: "按题目限制，通常150–250英文词",
    purpose: "一个答案只解决一个问题。科学问题题展现可检验性；挫折题展现调整；主动性题展现你作出的决定。",
    paragraphs: ["科学问题题：问题与重要性 → 明确假设 → 支持理由 → 可区分解释的实验及对照。不要提交泛泛领域综述。", "挫折题：实际困难 → 你的分析 → 具体行动 → 结果和认识。可以选研究重构，但必须写清真实改动。", "主动性题：原先缺口 → 主动提出的做法 → 如何验证 → 对项目的具体影响。", "Why program题：科学问题 → 所需训练 → 真实导师/平台 → 你的准备。人数和字段按网申要求。"],
    prompts: ["这是一个能够被证伪的假设吗？", "何种结果会使我放弃原解释？", "我提出的实验能排除哪一种替代解释？"],
    avoid: "不把方法清单当成研究问题；不写实验室资源无法支持的完整五年计划。",
    template: "题目：[粘贴网申题目]\n字数上限：[ ]\n\n问题/情境：[ ]\n重要性/困难：[ ]\n假设/我的决定：[ ]\n检验/行动与依据：[ ]\n可能结果/认识：[ ]"
  }
};

export const writingChecklist = [
  "先整理肿瘤、痛风、工具开发三个项目的事实：问题、个人贡献、实际结果、关键判断、局限。",
  "写中文SOP母稿：重点展开一个最能证明研究思考的项目，其他经历作为支持。",
  "写个人经历母稿：文献汇报、科学传播、跨领域学习和帮助同伴。",
  "按每校题目拆分科研陈述、社区短文和科学问题短答，英文稿最终对照字数/页数。",
  "逐项检查贡献、作者顺序、论文状态、日期与CV一致；推荐信材料已准备仍需推荐人在各系统提交。",
  "英文必须是你能解释并认可的表达；使用AI翻译或润色前查看该校当年文书AI政策。"
];
